import { FC, MouseEvent, useEffect, useMemo } from 'react'
import {
  AlignHorizontalLeft as AlignLeftIcon,
  AlignVerticalTop as AlignTopIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  VisibilityOffOutlined as HiddenIcon,
} from '@mui/icons-material'
import {
  ClickAwayListener,
  Divider,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material'

import { FORM_FIELD_LABEL_POSITION } from '@constants'

import { highlightActiveButton } from './helpers'

export type MenuPositionType = {
  mouseX: number
  mouseY: number
} | null

type EditTextPopoverProps = {
  menuPosition: MenuPositionType
  handleOpenEdit: (event: MouseEvent<HTMLLIElement>) => void
  elementTitleHidden?: boolean
  elementIsExpandable?: boolean
  labelPosition?: FORM_FIELD_LABEL_POSITION
  handleSetLabelPosition?: (value: FORM_FIELD_LABEL_POSITION) => void
  handleSetElementTitleHidden?: (value: boolean) => void
  handleSetIsExpandable?: (value: boolean) => void
  handleDeleteFieldOnForm?: () => void
  handleFieldMenuClose: () => void
  displayEditOption?: boolean
  displayDeleteOption?: boolean
  displayLabelParamsOption?: boolean
  displayLabelAlignmentOption?: boolean
  displayExpandableOption?: boolean
}

export const ElementContextMenu: FC<EditTextPopoverProps> = ({
  menuPosition,
  handleFieldMenuClose,
  handleOpenEdit,
  handleDeleteFieldOnForm,
  elementTitleHidden,
  elementIsExpandable,
  labelPosition,
  handleSetLabelPosition,
  handleSetElementTitleHidden,
  handleSetIsExpandable,
  displayEditOption = true,
  displayDeleteOption = true,
  displayLabelParamsOption = true,
  displayLabelAlignmentOption = true,
  displayExpandableOption = true,
}) => {
  const titleHidden: boolean = useMemo(() => elementTitleHidden as boolean, [elementTitleHidden])
  const isExpandable: boolean = useMemo(() => elementIsExpandable as boolean, [elementIsExpandable])

  useEffect(() => {
    if (!menuPosition) return

    const handleScroll = () => {
      handleFieldMenuClose()
    }

    window.addEventListener('scroll', handleScroll, true)

    return () => window.removeEventListener('scroll', handleScroll, true)
  }, [menuPosition, handleFieldMenuClose])

  return (
    <ClickAwayListener onClickAway={handleFieldMenuClose}>
      <Menu
        anchorReference='anchorPosition'
        open={menuPosition !== null}
        anchorPosition={
          menuPosition !== null
            ? { top: menuPosition.mouseY, left: menuPosition.mouseX }
            : undefined
        }
        slotProps={{
          paper: {
            style: { pointerEvents: 'auto' },
          },
          root: {
            style: {
              pointerEvents: 'none',
            },
          },
        }}
        onClose={handleFieldMenuClose}
      >
        {displayEditOption && (
          <MenuItem onClick={handleOpenEdit}>
            <Grid container gap={0.8} justifyContent='space-between'>
              <Typography variant='body1'>Edit</Typography>
              <EditIcon color='action' fontSize={'medium'} />
            </Grid>
          </MenuItem>
        )}
        {displayLabelParamsOption && (
          <Grid container justifyContent='center' px={1}>
            {displayLabelAlignmentOption && (
              <>
                <Tooltip title='Align label to left'>
                  <IconButton
                    sx={{
                      ...highlightActiveButton(
                        labelPosition === FORM_FIELD_LABEL_POSITION.LEFT && !titleHidden
                      ),
                    }}
                    onClick={() => handleSetLabelPosition?.(FORM_FIELD_LABEL_POSITION.LEFT)}
                  >
                    <AlignLeftIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Align label above field'>
                  <IconButton
                    sx={{
                      ...highlightActiveButton(
                        labelPosition === FORM_FIELD_LABEL_POSITION.TOP && !titleHidden
                      ),
                    }}
                    onClick={() => handleSetLabelPosition?.(FORM_FIELD_LABEL_POSITION.TOP)}
                  >
                    <AlignTopIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title='Hide label'>
              <IconButton
                sx={{ ...highlightActiveButton(titleHidden) }}
                onClick={() => handleSetElementTitleHidden?.(!titleHidden)}
              >
                <HiddenIcon />
              </IconButton>
            </Tooltip>
            {displayExpandableOption && (
              <Tooltip title='Make field expandable'>
                <IconButton
                  sx={{ ...highlightActiveButton(isExpandable) }}
                  onClick={() => handleSetIsExpandable?.(!isExpandable)}
                >
                  <ExpandLessIcon />
                </IconButton>
              </Tooltip>
            )}
          </Grid>
        )}
        {displayDeleteOption && (
          <>
            <Divider />
            <MenuItem onClick={handleDeleteFieldOnForm}>
              <Grid container gap={0.8} justifyContent='space-between'>
                <Typography variant='body1'>Delete</Typography>
                <DeleteIcon color='action' />
              </Grid>
            </MenuItem>
          </>
        )}
      </Menu>
    </ClickAwayListener>
  )
}
